import { useState } from "react";
import { LazyQueryExecFunction, useApolloClient } from "@apollo/client";
import { v1 as uuidv1 } from "uuid";
import { Dictionary, intersection, keyBy, omit } from "lodash";
import { DocumentType, gql } from "__generated__/gql";
import {
  AddressInput,
  DistributionCenterInput,
  PhoneNumberInput,
} from "__generated__/gql/graphql";
import {
  makeClearListFieldCache,
  makeClearCachedType,
} from "utils/apollo/cacheHelpers";
import { DocumentVariablesType } from "utils/apollo/hooks";
import { XlsxImportValidationError } from "utils/xlsxImportExport/XlsxImportErrors";
import type XlsxImportWorkBook from "utils/xlsxImportExport/XlsxImportWorkBook";
import {
  ImportValidatedData,
  ImportWorkSheetDefinitions,
} from "utils/xlsxDefinitions/customers";
import { conjunctionJoin } from "utils/formatting";
import { useCurrentBrandId } from "utils/currentUserHooks";
import { getHasAddressData } from "components/Shared/Forms/AddressFieldset";
import getChunkedTuple from "utils/getChunkedTuple";
import { DEFAULT_MAX_BYTE_SIZE } from "utils/misc";
import { assertBrandId } from "utils/assertions";

export function getCustomerNames(data: ImportValidatedData) {
  const names: string[] = [];

  data.customers.forEach((customer) => {
    if (!names.includes(customer.name)) {
      names.push(customer.name);
    }
  });

  data.warehouses.forEach((warehouse) => {
    if (!names.includes(warehouse.customerName)) {
      names.push(warehouse.customerName);
    }
  });

  return names;
}

export const existingQuery = gql(/* GraphQL */ `
  query CustomersImportExisting($brandId: UUID!) {
    orderingPlatformsForBrandList(brandId: $brandId) {
      id
      name
      forBrandId
      distributionCentersList {
        id
        name
        addressId
        phoneNumberId
      }
    }
  }
`);

type TExistingQuery = typeof existingQuery;

type ExistingQueryData = DocumentType<TExistingQuery>;

export function makeValidateDefaultCustomers(
  getExistingData: LazyQueryExecFunction<
    ExistingQueryData,
    DocumentVariablesType<TExistingQuery>
  >,
) {
  return async function validateDefaultCustomers(
    validatedData: ImportValidatedData,
    xlsxImportWorkBook: XlsxImportWorkBook<ImportWorkSheetDefinitions>,
  ) {
    const customerNames = getCustomerNames(validatedData);

    const { data: existingData } = await getExistingData();

    if (!existingData?.orderingPlatformsForBrandList) {
      throw new Error("Unexpected response getting existing customers");
    }

    const defaultCustomerNames =
      existingData.orderingPlatformsForBrandList.reduce((acc, customer) => {
        if (!customer.forBrandId) acc.push(customer.name);
        return acc;
      }, [] as string[]);

    const providedDefaultCustomerNames = intersection(
      customerNames,
      defaultCustomerNames,
    );

    if (providedDefaultCustomerNames.length > 0) {
      const namesStr = conjunctionJoin(
        providedDefaultCustomerNames.map((n) => `“${n}”`),
      );
      const title = "Provided file includes default customers";
      throw new XlsxImportValidationError(
        `${title}, which cannot be modified: ${namesStr}`,
        {
          title,
          description:
            "Default customers cannot be modified, but were included in the provided file.",
          helpText: `Please remove the following customers and their warehouses from the file to import: ${namesStr}`,
          workBook: xlsxImportWorkBook,
        },
      );
    }
  };
}

export function getExistingCounts(
  data: ImportValidatedData,
  existingData: ExistingQueryData,
) {
  const customerNames = getCustomerNames(data);

  const existingCustomerNames: string[] = [];

  const existingWarehousesByCustomer =
    existingData?.orderingPlatformsForBrandList?.reduce<{
      [customerName: string]: string[];
    }>((acc, customer) => {
      existingCustomerNames.push(customer.name);

      acc[customer.name] = [];

      customer.distributionCentersList.forEach((warehouse) => {
        if (warehouse.name) acc[customer.name].push(warehouse.name);
      });

      return acc;
    }, {});

  const existingWarehousesIntersection = data.warehouses.filter((warehouse) => {
    const existingWarehousesForCustomer =
      existingWarehousesByCustomer?.[warehouse.customerName];
    return !!existingWarehousesForCustomer?.includes(warehouse.name);
  });

  return {
    customerNames,
    existingCustomerNames: intersection(customerNames, existingCustomerNames),
    existingWarehouses: existingWarehousesIntersection,
  };
}

const importWarehouseDependenciesMutation = gql(/* GraphQL */ `
  mutation ImportWarehouseDependencies(
    $orderingPlatforms: [OrderingPlatformInput!] = []
    $addresses: [AddressInput!] = []
    $phoneNumbers: [PhoneNumberInput!] = []
    $names: [String!] = []
    $brandId: UUID!
    $includeAllOrderingPlatforms: Boolean = false
  ) {
    upsertOrderingPlatformsByNameAndForBrandId(
      input: { orderingPlatforms: $orderingPlatforms }
    ) {
      query {
        orderingPlatformsForBrandList(
          brandId: $brandId
          filter: { name: { in: $names } }
        ) @include(if: $includeAllOrderingPlatforms) {
          id
          name
        }
      }
    }
    upsertAddressesById(input: { addresses: $addresses }) {
      addresses {
        id
      }
    }
    upsertPhoneNumbersById(input: { phoneNumbers: $phoneNumbers }) {
      phoneNumbers {
        id
      }
    }
  }
`);

type NewOrderingPlatforms = NonNullable<
  NonNullable<
    DocumentType<
      typeof importWarehouseDependenciesMutation
    >["upsertOrderingPlatformsByNameAndForBrandId"]
  >["query"]
>["orderingPlatformsForBrandList"];

export const importWarehousesMutation = gql(/* GraphQL */ `
  mutation ImportWarehouses($distributionCenters: [DistributionCenterInput!]!) {
    upsertDistributionCentersByOrderingPlatformIdAndName(
      input: { distributionCenters: $distributionCenters }
    ) {
      distributionCenters {
        id
      }
    }
  }
`);

type ByCustomerByWarehouse<V> = {
  [customer: string]: {
    [warehouse: string]: V;
  };
};

type AddressPhoneIds = {
  addressId?: string | null;
  phoneNumberId?: string | null;
};

type UsePerformImportReturnTuple = [
  performImport: (
    data: ImportValidatedData,
    existingData: ExistingQueryData,
  ) => Promise<void>,
  context: { isLoading: boolean },
];

export function usePerformImport(): UsePerformImportReturnTuple {
  const client = useApolloClient();
  const [isLoading, setIsLoading] = useState(false);
  const brandId = useCurrentBrandId();

  async function performImport(
    data: ImportValidatedData,
    existingData: ExistingQueryData,
  ) {
    setIsLoading(true);

    try {
      assertBrandId(brandId);

      if (!existingData.orderingPlatformsForBrandList) {
        throw new Error("Invalid response returned for existing data");
      }

      const customerNames = getCustomerNames(data);

      const orderingPlatforms = customerNames.map((name) => ({
        name,
        forBrandId: brandId,
      }));

      const existingAddressPhoneIdsByCustomerByWarehouse =
        existingData.orderingPlatformsForBrandList.reduce<
          ByCustomerByWarehouse<AddressPhoneIds>
        >((acc, customer) => {
          acc[customer.name] = customer.distributionCentersList.reduce<
            Dictionary<AddressPhoneIds>
          >((acc2, warehouse) => {
            const { name, addressId, phoneNumberId } = warehouse;
            if (name) {
              // eslint-disable-next-line no-param-reassign
              acc2[name] = { addressId, phoneNumberId };
            }
            return acc2;
          }, {});
          return acc;
        }, {});

      const addresses: AddressInput[] = [];
      const phoneNumbers: PhoneNumberInput[] = [];

      const addressPhoneNumberIdsByCustomerByWarehouse = data.warehouses.reduce<
        ByCustomerByWarehouse<AddressPhoneIds>
      >((acc, warehouse) => {
        const {
          customerName,
          name,
          addressLine1,
          addressLine2,
          addressCity,
          addressState,
          addressZip,
          phone,
        } = warehouse;

        const existingIds =
          existingAddressPhoneIdsByCustomerByWarehouse[customerName]?.[name];

        if (!acc[customerName]) acc[customerName] = {};
        if (!acc[customerName][name]) acc[customerName][name] = {};

        const addressObj: AddressInput = {
          line1: addressLine1 ?? null,
          line2: addressLine2 ?? null,
          city: addressCity ?? null,
          state: addressState ?? null,
          zip: addressZip ?? null,
        };

        if (getHasAddressData(addressObj)) {
          const id = existingIds?.addressId || uuidv1();
          addresses.push({ ...addressObj, id });
          acc[customerName][name].addressId = id;
        } else {
          acc[customerName][name].addressId = null;
        }

        if (phone) {
          const id = existingIds?.phoneNumberId || uuidv1();
          phoneNumbers.push({ id, phone });
          acc[customerName][name].phoneNumberId = id;
        } else {
          acc[customerName][name].phoneNumberId = null;
        }

        return acc;
      }, {});

      const chunkedDepsVars = getChunkedTuple(
        [orderingPlatforms, addresses, phoneNumbers],
        { maxByteSize: DEFAULT_MAX_BYTE_SIZE - 1000 },
      );

      const newOrderingPlatforms = await chunkedDepsVars.reduce(
        async (
          prev,
          [chunkedOrderingPlatforms, chunkedAddresses, chunkedPhoneNumbers],
          idx,
        ) => {
          await prev;

          const isLast = idx === chunkedDepsVars.length - 1;

          const { data: depsData } = await client.mutate({
            mutation: importWarehouseDependenciesMutation,
            variables: {
              brandId,
              names: customerNames,
              orderingPlatforms: chunkedOrderingPlatforms,
              addresses: chunkedAddresses,
              phoneNumbers: chunkedPhoneNumbers,
              includeAllOrderingPlatforms: isLast,
            },
          });

          return depsData?.upsertOrderingPlatformsByNameAndForBrandId?.query
            ?.orderingPlatformsForBrandList;
        },
        Promise.resolve(undefined as NewOrderingPlatforms),
      );

      if (!newOrderingPlatforms) {
        throw new Error(
          "Unexpected response returned when importing customers",
        );
      }

      const orderingPlatformsByName = keyBy(newOrderingPlatforms, "name");

      const distributionCenters = data.warehouses.map<DistributionCenterInput>(
        (warehouse) => {
          const { customerName, ...dcData } = omit(warehouse, [
            "addressLine1",
            "addressLine2",
            "addressCity",
            "addressState",
            "addressZip",
            "phone",
          ]);

          const orderingPlatformId =
            orderingPlatformsByName[warehouse.customerName]?.id;

          if (!orderingPlatformId)
            throw new Error("Could not determine customer ID for warehouse");

          const addressPhoneIds =
            addressPhoneNumberIdsByCustomerByWarehouse[customerName]?.[
              warehouse.name
            ];

          return { ...dcData, ...addressPhoneIds, orderingPlatformId };
        },
      );

      const chunkedDcs = getChunkedTuple([distributionCenters], {
        maxByteSize: DEFAULT_MAX_BYTE_SIZE - 1000,
      });

      await chunkedDcs.reduce(async (prev, [chunkedDistributionCenters]) => {
        await prev;

        if (!chunkedDistributionCenters) return;

        await client.mutate({
          mutation: importWarehousesMutation,
          variables: { distributionCenters: chunkedDistributionCenters },
        });
      }, Promise.resolve());

      const { cache } = client;
      cache.modify({
        fields: makeClearListFieldCache("orderingPlatforms"),
      });
      cache.modify({
        fields: makeClearListFieldCache("orderingPlatformsForBrand"),
      });
      cache.modify({ fields: makeClearListFieldCache("distributionCenters") });
      const clearType = makeClearCachedType(cache);
      clearType("OrderingPlatform");
      clearType("DistributionCenter");
      cache.gc();
    } finally {
      setIsLoading(false);
    }
  }

  return [performImport, { isLoading }];
}
